import { artifactExtension } from './artifact-file-meta';

/** Whether a generated artifact is worth offering an inline preview for.
 *
 *  Deliberately a denylist, not an allowlist: preview is offered for every
 *  format except the ones known to have no viable inline rendering. A new
 *  format the sandbox starts writing therefore gets a preview affordance by
 *  default and degrades to the panel's download card if nothing can render it,
 *  rather than silently losing the affordance until someone remembers to add it
 *  to a list.
 *
 *  The excluded families are the ones where a preview could only ever be a dead
 *  end:
 *   - archives, whose content is other files, not a document;
 *   - legacy binary Excel, which the OOXML-only parser cannot read at all
 *     (the breakage behind OMNI-838).
 *
 *  Note this is about *generated* artifacts. Citation-side previewability is a
 *  separate, stricter question answered by `resolveFilePreviewKind` in the
 *  console app, which downgrades all spreadsheets because uploaded files can be
 *  any of the legacy variants. Generated artifacts are a set we control, so
 *  `.xlsx`/`.csv` are previewable here.
 */
const NON_PREVIEWABLE_EXTENSIONS = new Set([
  // Archives: a preview would list files, not show a document.
  'zip',
  '7z',
  'rar',
  'tar',
  'gz',
  'tgz',
  'bz2',
  // Legacy binary Excel: ExcelJS is OOXML-only, so these render blank or throw.
  'xls',
  'xlsm',
  'xlsb',
]);

/** False only for formats with no viable inline rendering, so the caller can
 *  hide the preview affordance entirely rather than offering an eye icon that
 *  opens a panel with nothing in it. */
export function canPreviewArtifact(filename: string): boolean {
  const ext = artifactExtension(filename);
  // A file with no extension tells us nothing about how to render it.
  if (!ext) return false;
  return !NON_PREVIEWABLE_EXTENSIONS.has(ext);
}
